/**
 * CoordinateErrorHandler for coordinate mapping errors with graceful fallbacks
 * Requirements: 3.4
 */

import React from 'react';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import WarningIcon from '@mui/icons-material/Warning';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { BoundingBox } from '../../types';

interface CoordinateErrorHandlerProps {
  error: Error | string;
  boundingBoxes?: BoundingBox[];
  fieldId?: string;
  onDisableHighlighting?: () => void;
  onRetryMapping?: () => void;
}

/**
 * Handles coordinate mapping errors with graceful fallbacks
 * Allows users to continue working even when highlighting fails
 */
export function CoordinateErrorHandler({
  error,
  boundingBoxes,
  fieldId,
  onDisableHighlighting,
  onRetryMapping,
}: CoordinateErrorHandlerProps) {
  const errorMessage = typeof error === 'string' ? error : error.message;

  const getCoordinateErrorDetails = (errorMsg: string) => {
    const errorPatterns = [
      {
        pattern: /invalid.*coordinate|coordinate.*invalid/i,
        title: 'Invalid Coordinates',
        message: 'The document coordinates for this field are invalid or corrupted.',
        severity: 'warning' as const,
      },
      {
        pattern: /viewport.*not.*available|viewport.*null/i,
        title: 'PDF Not Ready',
        message: 'The PDF document is not fully loaded yet. Highlighting will be available once loading completes.',
        severity: 'info' as const,
      },
      {
        pattern: /transformation.*failed|transform.*error/i,
        title: 'Coordinate Transformation Failed',
        message: 'Unable to convert document coordinates to screen coordinates.',
        severity: 'warning' as const,
      },
      {
        pattern: /out.*of.*bounds|bounds.*exceeded/i,
        title: 'Coordinates Out of Bounds',
        message: 'The field coordinates are outside the document boundaries.',
        severity: 'warning' as const,
      },
    ];

    const matchedPattern = errorPatterns.find(pattern => 
      pattern.pattern.test(errorMsg)
    );

    return matchedPattern || {
      title: 'Highlighting Error',
      message: 'An error occurred while displaying field highlights.',
      severity: 'warning' as const,
    };
  };

  const errorDetails = getCoordinateErrorDetails(errorMessage);

  return (
    <Alert
      severity={errorDetails.severity}
      icon={<WarningIcon />}
      sx={{ mb: 1 }}
      action={
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {onRetryMapping && (
            <Button
              color="inherit"
              size="small"
              onClick={onRetryMapping}
            >
              Retry
            </Button>
          )}
          {onDisableHighlighting && (
            <Button
              color="inherit"
              size="small"
              startIcon={<VisibilityOffIcon />}
              onClick={onDisableHighlighting}
            >
              Disable Highlighting
            </Button>
          )}
        </Box>
      }
    >
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>{errorDetails.title}:</strong> {errorDetails.message}
      </Typography>
      
      {fieldId && (
        <Typography variant="caption" color="text.secondary">
          Field: {fieldId}
        </Typography>
      )}
      
      {boundingBoxes && boundingBoxes.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Affected coordinates: {boundingBoxes.length} region(s)
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
            {boundingBoxes.slice(0, 3).map((bbox, index) => (
              <Chip
                key={index}
                label={`Page ${bbox.page}`}
                size="small"
                variant="outlined"
                color="warning"
              />
            ))}
            {boundingBoxes.length > 3 && (
              <Chip
                label={`+${boundingBoxes.length - 3} more`}
                size="small"
                variant="outlined"
                color="warning"
              />
            )}
          </Box>
        </Box>
      )}
    </Alert>
  );
}

/**
 * Hook for handling coordinate mapping errors with graceful fallbacks
 */
export function useCoordinateErrorHandler() {
  const [errors, setErrors] = React.useState<Map<string, Error | string>>(new Map());
  const [disabledFields, setDisabledFields] = React.useState<Set<string>>(new Set());

  const handleCoordinateError = React.useCallback((
    fieldId: string, 
    error: Error | string,
    boundingBoxes?: BoundingBox[]
  ) => {
    console.warn(`Coordinate mapping error for field ${fieldId}:`, error);
    
    setErrors(prev => new Map(prev).set(fieldId, error));

    // Log error details for debugging
    const errorReport = {
      fieldId,
      error: typeof error === 'string' ? error : error.message,
      boundingBoxes: boundingBoxes?.length || 0,
      timestamp: new Date().toISOString(),
    };
    
    console.log('Coordinate Error Report:', errorReport);
  }, []);

  const clearError = React.useCallback((fieldId: string) => {
    setErrors(prev => {
      const newErrors = new Map(prev);
      newErrors.delete(fieldId);
      return newErrors;
    });
  }, []);

  const disableHighlighting = React.useCallback((fieldId: string) => {
    setDisabledFields(prev => new Set(prev).add(fieldId));
    clearError(fieldId);
  }, [clearError]);

  const enableHighlighting = React.useCallback((fieldId: string) => {
    setDisabledFields(prev => {
      const newDisabled = new Set(prev);
      newDisabled.delete(fieldId);
      return newDisabled;
    });
  }, []);

  const retryMapping = React.useCallback((fieldId: string) => {
    clearError(fieldId);
    enableHighlighting(fieldId);
  }, [clearError, enableHighlighting]);

  const isHighlightingDisabled = React.useCallback((fieldId: string) => {
    return disabledFields.has(fieldId);
  }, [disabledFields]);

  const getFieldError = React.useCallback((fieldId: string) => {
    return errors.get(fieldId);
  }, [errors]);

  const hasErrors = errors.size > 0;

  return {
    errors,
    disabledFields,
    hasErrors,
    handleCoordinateError,
    clearError,
    disableHighlighting,
    enableHighlighting,
    retryMapping,
    isHighlightingDisabled,
    getFieldError,
  };
}

/**
 * Utility function to validate bounding boxes and provide detailed error information
 */
export function validateBoundingBoxWithError(bbox: BoundingBox): { isValid: boolean; error?: string } {
  if (!bbox) {
    return { isValid: false, error: 'Bounding box is null or undefined' };
  }

  if (typeof bbox.x !== 'number' || typeof bbox.y !== 'number' || 
      typeof bbox.width !== 'number' || typeof bbox.height !== 'number') {
    return { isValid: false, error: 'Bounding box coordinates must be numbers' };
  }

  if (bbox.x < 0 || bbox.x > 1 || bbox.y < 0 || bbox.y > 1) {
    return { isValid: false, error: 'Bounding box coordinates must be normalized (0-1)' };
  }

  if (bbox.width <= 0 || bbox.height <= 0) {
    return { isValid: false, error: 'Bounding box dimensions must be positive' };
  }

  if (bbox.x + bbox.width > 1 || bbox.y + bbox.height > 1) {
    return { isValid: false, error: 'Bounding box extends beyond document boundaries' };
  }

  if (typeof bbox.page !== 'number' || bbox.page < 1) {
    return { isValid: false, error: 'Bounding box page number must be a positive integer' };
  }

  return { isValid: true };
}

export default CoordinateErrorHandler;